import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Ban, Car, Check, Download, LogIn, Plus, QrCode, RefreshCw, Search, X } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";
import { useAuth } from "../../context/AuthContext";
import { useApiMessages } from "../../hooks/useApiMessages";
import { useVisitorScope } from "../../components/visitors/useVisitorScope";
import {
  effectivePreauthStatus,
  NoFacilitiesNotice,
  PageHeader,
  ScopeFilters,
  StatusBadge,
} from "../../components/visitors/VisitorShared";
import { CheckInDialog, HostField, VehicleFields, VisitorFields, VisitorSearch } from "../../components/visitors/VisitorForms";
import { fmtDateTime, localInputToIso, toLocalInput, tzOffset, visitorApi } from "../../components/visitors/visitorUtils";
import { buildPassImage } from "../../components/visitors/VisitorPassCard";

const STATUS_FILTERS = ["active", "pending_approval", "approved", "rejected", "revoked", "cancelled", "all"];
const EMPTY_VISITOR = { full_name: "", id_type: "national_id", id_number: "", visitor_company: "", phone: "", email: "" };

export default function Preauthorizations() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { getErrorMessage } = useApiMessages();
  const scope = useVisitorScope();
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active");
  const [date, setDate] = useState("");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [qrFor, setQrFor] = useState(null);
  const [decision, setDecision] = useState(null); // { preauth, action }
  const [reason, setReason] = useState("");
  const [checkInPreset, setCheckInPreset] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    const status = statusFilter === "active" ? "pending_approval,approved" : statusFilter;
    try {
      setItems(
        await visitorApi.get("/preauths", {
          ...scope.params,
          status,
          date,
          search,
          tz_offset_minutes: tzOffset(),
        })
      );
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.loadError"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.companyId, scope.facilityId, statusFilter, date, search]);

  useEffect(() => {
    const handle = setTimeout(load, 250);
    return () => clearTimeout(handle);
  }, [load]);

  const openDetail = async (p) => {
    try {
      setDetail(await visitorApi.get(`/preauths/${p.id}`));
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.loadError"));
    }
  };

  const openQr = async (p) => {
    try {
      setQrFor(await visitorApi.get(`/preauths/${p.id}`));
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.loadError"));
    }
  };

  const submitDecision = async () => {
    const { preauth, action } = decision;
    try {
      await visitorApi.post(`/preauths/${preauth.id}/${action}`, { reason: reason || null });
      toast.success(t(`visitors.preauths.${action}Done`));
      setDecision(null);
      setReason("");
      load();
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.saveError"));
    }
  };

  const canCancel = (p) => scope.isManager || p.created_by === user?.id;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("visitors.preauths.title")}
        subtitle={t("visitors.preauths.subtitle")}
        actions={
          <>
            <Button variant="outline" size="icon" onClick={load} aria-label={t("visitors.common.refresh")}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button onClick={() => setCreateOpen(true)} disabled={scope.facilities.length === 0}>
              <Plus className="h-4 w-4 mr-2" />
              {t("visitors.preauths.newPreauth")}
            </Button>
          </>
        }
      />

      <ScopeFilters scope={scope}>
        <div>
          <Label className="text-sm text-slate-600 mb-1 block">{t("visitors.common.date")}</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <Label className="text-sm text-slate-600 mb-1 block">{t("visitors.common.search")}</Label>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
      </ScopeFilters>
      {scope.loaded && scope.facilities.length === 0 && <NoFacilitiesNotice isManager={scope.isManager} />}

      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList className="flex-wrap h-auto">
          {STATUS_FILTERS.map((s) => (
            <TabsTrigger key={s} value={s}>
              {t(`visitors.preauths.filters.${s}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-0 sm:p-2">
          {items.length === 0 ? (
            <p className="text-center py-10 text-slate-500">{t("visitors.common.noResults")}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("visitors.common.visitor")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("visitors.common.host")}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t("visitors.common.facility")}</TableHead>
                  <TableHead>{t("visitors.preauths.validity")}</TableHead>
                  <TableHead>{t("visitors.common.status")}</TableHead>
                  <TableHead className="text-right">{t("visitors.common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p) => {
                  const status = effectivePreauthStatus(p);
                  return (
                    <TableRow key={p.id} className="cursor-pointer" onClick={() => openDetail(p)}>
                      <TableCell>
                        <p className="font-medium">{p.visitor_name}</p>
                        <p className="text-xs text-slate-500">
                          {[p.visitor_company, p.purpose].filter(Boolean).join(" · ")}
                          {p.vehicle?.plate && (
                            <span className="inline-flex items-center gap-1 ml-1">
                              <Car className="h-3 w-3" />
                              {p.vehicle.plate}
                            </span>
                          )}
                        </p>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{p.host_name || "—"}</TableCell>
                      <TableCell className="hidden lg:table-cell">{p.facility_name}</TableCell>
                      <TableCell className="text-xs">
                        <p>{fmtDateTime(p.valid_from)}</p>
                        <p>{fmtDateTime(p.valid_until)}</p>
                        <p className="text-slate-500">
                          {t("visitors.scan.uses")}: {p.uses}/{p.max_uses}
                          {p.is_recurring && ` · ${t("visitors.preauths.recurring")}`}
                        </p>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={status} />
                        {p.decision_reason && <p className="text-xs text-slate-500 mt-1">{p.decision_reason}</p>}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end flex-wrap gap-1">
                          {status === "approved" && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => openQr(p)} title={t("visitors.preauths.showQr")}>
                                <QrCode className="h-4 w-4" />
                              </Button>
                              <Button size="sm" onClick={() => setCheckInPreset({ preauth: p })} title={t("visitors.common.checkIn")}>
                                <LogIn className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {scope.isManager && p.status === "pending_approval" && (
                            <>
                              <Button size="sm" variant="outline" className="text-green-700" onClick={() => setDecision({ preauth: p, action: "approve" })}>
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => setDecision({ preauth: p, action: "reject" })}>
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {scope.isManager && p.status === "approved" && (
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setDecision({ preauth: p, action: "revoke" })} title={t("visitors.preauths.revoke")}>
                              <Ban className="h-4 w-4" />
                            </Button>
                          )}
                          {!scope.isManager && p.status === "pending_approval" && canCancel(p) && (
                            <Button size="sm" variant="ghost" onClick={() => setDecision({ preauth: p, action: "cancel" })}>
                              {t("visitors.preauths.cancelVisit")}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PreauthCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        scope={scope}
        onDone={(p) => {
          load();
          if (p.status === "approved") openQr(p);
        }}
      />

      <PreauthDetailDialog
        preauth={detail}
        onOpenChange={(o) => !o && setDetail(null)}
        isManager={scope.isManager}
        canCancel={detail ? canCancel(detail) : false}
        onAction={(action) => {
          const p = detail;
          setDetail(null);
          if (action === "qr") openQr(p);
          else if (action === "checkin") setCheckInPreset({ preauth: p });
          else setDecision({ preauth: p, action });
        }}
      />

      <QrDialog preauth={qrFor} onOpenChange={(o) => !o && setQrFor(null)} isManager={scope.isManager} onRegenerated={setQrFor} />

      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{decision && t(`visitors.preauths.${decision.action}`)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="font-medium">{decision?.preauth.visitor_name}</p>
            <Label>{t("visitors.preauths.reason")}</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision(null)}>
              {t("visitors.common.cancel")}
            </Button>
            <Button variant={decision?.action === "approve" ? "default" : "destructive"} onClick={submitDecision}>
              {t("visitors.common.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {checkInPreset && (
        <CheckInDialog
          open={!!checkInPreset}
          onOpenChange={(o) => !o && setCheckInPreset(null)}
          scope={scope}
          preset={checkInPreset}
          onDone={load}
        />
      )}
    </div>
  );
}

const PreauthCreateDialog = ({ open, onOpenChange, scope, onDone }) => {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const [mode, setMode] = useState("existing");
  const [visitor, setVisitor] = useState(null);
  const [newVisitor, setNewVisitor] = useState(EMPTY_VISITOR);
  const [form, setForm] = useState({});
  const [withVehicle, setWithVehicle] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const now = new Date();
    setMode("existing");
    setVisitor(null);
    setNewVisitor(EMPTY_VISITOR);
    setForm({
      facility_id: scope.facilityId !== "all" ? scope.facilityId : scope.facilities[0]?.id || "",
      host_id: null,
      host_name: null,
      destination: "",
      purpose: "",
      valid_from: toLocalInput(now),
      valid_until: toLocalInput(new Date(now.getTime() + 4 * 3600000)),
      max_uses: 1,
      is_recurring: false,
      notes: "",
    });
    setWithVehicle(false);
    setVehicle(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const facility = scope.facilities.find((f) => f.id === form.facility_id);

  const handleSave = async () => {
    if (!form.facility_id) return toast.error(t("visitors.common.selectFacility"));
    if (mode === "existing" && !visitor) return toast.error(t("visitors.access.selectVisitorFirst"));
    if (mode === "new" && !newVisitor.full_name.trim()) return toast.error(t("visitors.access.nameRequired"));
    if (withVehicle && !vehicle?.plate) return toast.error(t("visitors.access.plateRequired"));
    setSaving(true);
    try {
      const created = await visitorApi.post("/preauths", {
        ...form,
        valid_from: localInputToIso(form.valid_from),
        valid_until: localInputToIso(form.valid_until),
        max_uses: Number(form.max_uses) || 1,
        visitor_id: mode === "existing" ? visitor.id : null,
        visitor: mode === "new" ? { ...newVisitor, id_number: newVisitor.id_number || null } : null,
        vehicle: withVehicle ? vehicle : null,
      });
      toast.success(
        created.status === "approved" ? t("visitors.preauths.createdApproved") : t("visitors.preauths.createdPending")
      );
      onOpenChange(false);
      onDone?.(created);
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("visitors.preauths.newPreauth")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <Label className="mb-1 block">{t("visitors.common.facility")} *</Label>
            <Select value={form.facility_id} onValueChange={(v) => set("facility_id", v)}>
              <SelectTrigger>
                <SelectValue placeholder={t("visitors.common.selectFacility")} />
              </SelectTrigger>
              <SelectContent>
                {scope.facilities.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <section className="space-y-3">
            <h3 className="font-semibold">{t("visitors.common.visitor")}</h3>
            <Tabs value={mode} onValueChange={setMode}>
              <TabsList>
                <TabsTrigger value="existing">{t("visitors.access.existingVisitor")}</TabsTrigger>
                <TabsTrigger value="new">{t("visitors.access.newVisitorTab")}</TabsTrigger>
              </TabsList>
            </Tabs>
            {mode === "existing" &&
              (visitor ? (
                <div className="rounded-lg border p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{visitor.full_name}</p>
                    <p className="text-sm text-slate-500">{[visitor.id_number, visitor.visitor_company].filter(Boolean).join(" · ")}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setVisitor(null)}>
                    {t("visitors.access.change")}
                  </Button>
                </div>
              ) : (
                <VisitorSearch companyId={facility?.company_id || scope.effectiveCompanyId} onSelect={setVisitor} />
              ))}
            {mode === "new" && <VisitorFields value={newVisitor} onChange={setNewVisitor} withPhoto={false} />}
          </section>

          <section className="space-y-3">
            <h3 className="font-semibold">{t("visitors.access.visitData")}</h3>
            <HostField
              companyId={facility?.company_id || scope.effectiveCompanyId}
              facilityId={form.facility_id}
              hostId={form.host_id}
              hostName={form.host_name}
              onChange={(h) => setForm((f) => ({ ...f, ...h }))}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block">{t("visitors.common.destination")}</Label>
                <Input value={form.destination || ""} onChange={(e) => set("destination", e.target.value)} />
              </div>
              <div>
                <Label className="mb-1 block">{t("visitors.common.purpose")}</Label>
                <Input value={form.purpose || ""} onChange={(e) => set("purpose", e.target.value)} />
              </div>
              <div>
                <Label className="mb-1 block">{t("visitors.preauths.validFrom")} *</Label>
                <Input type="datetime-local" value={form.valid_from || ""} onChange={(e) => set("valid_from", e.target.value)} />
              </div>
              <div>
                <Label className="mb-1 block">{t("visitors.preauths.validUntil")} *</Label>
                <Input type="datetime-local" value={form.valid_until || ""} onChange={(e) => set("valid_until", e.target.value)} />
              </div>
              <div>
                <Label className="mb-1 block">{t("visitors.preauths.maxUses")}</Label>
                <Input type="number" min={1} value={form.max_uses || 1} onChange={(e) => set("max_uses", e.target.value)} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer self-end pb-2">
                <Checkbox
                  checked={!!form.is_recurring}
                  onCheckedChange={(c) => setForm((f) => ({ ...f, is_recurring: !!c, max_uses: c && f.max_uses < 2 ? 30 : f.max_uses }))}
                />
                <span className="text-sm">{t("visitors.preauths.recurringHelp")}</span>
              </label>
            </div>
          </section>

          <section className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={withVehicle} onCheckedChange={(c) => setWithVehicle(!!c)} />
              <span className="font-medium">{t("visitors.access.addVehicle")}</span>
            </label>
            {withVehicle && <VehicleFields value={vehicle} onChange={setVehicle} />}
          </section>

          <div>
            <Label className="mb-1 block">{t("visitors.common.notes")}</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
          </div>
          {!scope.isManager && <p className="text-sm text-amber-700">{t("visitors.preauths.needsApproval")}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("visitors.common.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {t("visitors.common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Info = ({ label, value, className }) => (
  <div className={`min-w-0 ${className || ""}`}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className="text-sm font-medium text-slate-900 break-words whitespace-pre-wrap">{value || "—"}</p>
  </div>
);

/** Detalle completo de una preautorización con sus usos y acciones disponibles. */
const PreauthDetailDialog = ({ preauth, onOpenChange, isManager, canCancel, onAction }) => {
  const { t } = useTranslation();
  if (!preauth) return null;
  const status = effectivePreauthStatus(preauth);
  const v = preauth.vehicle;

  return (
    <Dialog open={!!preauth} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("visitors.preauths.detail")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold">{preauth.visitor_name}</h3>
              <StatusBadge status={status} />
              {preauth.is_recurring && <Badge variant="secondary">{t("visitors.preauths.recurring")}</Badge>}
            </div>
            <p className="text-sm text-slate-500">
              {[
                preauth.visitor_id_type && t(`visitors.idTypes.${preauth.visitor_id_type}`),
                preauth.visitor_id_number,
                preauth.visitor_company,
                preauth.visitor_phone,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 rounded-lg border p-4">
            <Info label={t("visitors.common.facility")} value={preauth.facility_name} />
            <Info label={t("visitors.common.host")} value={[preauth.host_name, preauth.host_unit].filter(Boolean).join(" · ")} />
            <Info label={t("visitors.common.destination")} value={preauth.destination} />
            <Info label={t("visitors.common.purpose")} value={preauth.purpose} />
            <Info label={t("visitors.preauths.validFrom")} value={fmtDateTime(preauth.valid_from)} />
            <Info label={t("visitors.preauths.validUntil")} value={fmtDateTime(preauth.valid_until)} />
            <Info label={t("visitors.scan.uses")} value={`${preauth.uses}/${preauth.max_uses}`} />
            <Info label={t("visitors.preauths.lastUsed")} value={preauth.last_used_at ? fmtDateTime(preauth.last_used_at) : null} />
            <Info
              label={t("visitors.common.vehicle")}
              value={v ? [v.plate, v.make, v.model, v.color].filter(Boolean).join(" · ") : null}
            />
            <Info
              label={t("visitors.preauths.createdBy")}
              value={`${preauth.created_by_name || "—"} · ${fmtDateTime(preauth.created_at)}`}
            />
            {preauth.decided_at && (
              <Info
                label={t("visitors.preauths.decidedBy")}
                value={`${preauth.decided_by_name || "—"} · ${fmtDateTime(preauth.decided_at)}`}
              />
            )}
            {preauth.decision_reason && <Info label={t("visitors.preauths.decisionReason")} value={preauth.decision_reason} />}
            {preauth.notes && <Info label={t("visitors.common.notes")} value={preauth.notes} className="col-span-2 sm:col-span-3" />}
          </div>

          <section>
            <h4 className="font-semibold mb-2">
              {t("visitors.preauths.usedVisits")} ({preauth.visits?.length || 0})
            </h4>
            {!preauth.visits?.length ? (
              <p className="text-sm text-slate-500">{t("visitors.preauths.noUses")}</p>
            ) : (
              <div className="border rounded-lg divide-y">
                {preauth.visits.map((visit) => (
                  <div key={visit.id} className="px-3 py-2 flex items-center justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium">
                        {fmtDateTime(visit.check_in_at || visit.created_at)}
                        {visit.check_out_at && ` → ${fmtDateTime(visit.check_out_at)}`}
                      </p>
                      <p className="text-xs text-slate-500">
                        {[visit.access_point, visit.check_in_by_name].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <StatusBadge status={visit.status} />
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {status === "approved" && (
            <>
              {preauth.qr_value && (
                <Button variant="outline" onClick={() => onAction("qr")}>
                  <QrCode className="h-4 w-4 mr-2" />
                  {t("visitors.preauths.showQr")}
                </Button>
              )}
              {isManager && (
                <Button variant="outline" className="text-red-600" onClick={() => onAction("revoke")}>
                  <Ban className="h-4 w-4 mr-2" />
                  {t("visitors.preauths.revoke")}
                </Button>
              )}
              <Button onClick={() => onAction("checkin")}>
                <LogIn className="h-4 w-4 mr-2" />
                {t("visitors.access.registerEntry")}
              </Button>
            </>
          )}
          {isManager && preauth.status === "pending_approval" && (
            <>
              <Button variant="outline" className="text-red-600" onClick={() => onAction("reject")}>
                <X className="h-4 w-4 mr-2" />
                {t("visitors.preauths.reject")}
              </Button>
              <Button onClick={() => onAction("approve")}>
                <Check className="h-4 w-4 mr-2" />
                {t("visitors.preauths.approve")}
              </Button>
            </>
          )}
          {!isManager && preauth.status === "pending_approval" && canCancel && (
            <Button variant="outline" onClick={() => onAction("cancel")}>
              {t("visitors.preauths.cancel")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Pase de visita con formato de carnet; la descarga es la misma imagen. */
const QrDialog = ({ preauth, onOpenChange, isManager, onRegenerated }) => {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const qrRef = useRef(null);
  const [image, setImage] = useState(null);

  useEffect(() => {
    setImage(null);
    if (!preauth?.qr_value) return;
    let cancelled = false;
    // Espera a que el QR oculto se dibuje antes de componer el carnet
    const id = setTimeout(async () => {
      const qrCanvas = qrRef.current?.querySelector("canvas");
      const url = await buildPassImage({
        preauth,
        qrCanvas,
        labels: {
          title: t("visitors.preauths.qrTitle"),
          host: t("visitors.common.host"),
          validFrom: t("visitors.preauths.validFrom"),
          validUntil: t("visitors.preauths.validUntil"),
          footer: t("visitors.preauths.passFooter"),
        },
      });
      if (!cancelled) setImage(url);
    }, 50);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [preauth, t]);

  const download = () => {
    if (!image) return;
    const a = document.createElement("a");
    a.href = image;
    a.download = `carnet-visita-${preauth.visitor_name.replace(/\s+/g, "-")}.png`;
    a.click();
  };

  const regenerate = async () => {
    if (!window.confirm(t("visitors.preauths.regenerateConfirm"))) return;
    try {
      const res = await visitorApi.post(`/preauths/${preauth.id}/regenerate-qr`);
      onRegenerated({ ...preauth, qr_value: res.qr_value });
      toast.success(t("visitors.preauths.regenerated"));
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.saveError"));
    }
  };

  return (
    <Dialog open={!!preauth} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("visitors.preauths.qrTitle")}</DialogTitle>
        </DialogHeader>
        {preauth?.qr_value ? (
          <div className="flex flex-col items-center gap-3 py-1 text-center">
            <div ref={qrRef} className="hidden">
              <QRCodeCanvas value={preauth.qr_value} size={500} level="M" includeMargin />
            </div>
            {image ? (
              <img src={image} alt={preauth.visitor_name} className="w-64 rounded-2xl shadow-xl border" />
            ) : (
              <div className="w-64 aspect-[16/25] rounded-2xl bg-slate-100 animate-pulse" />
            )}
            <p className="text-xs text-slate-500">{t("visitors.preauths.qrHelp")}</p>
          </div>
        ) : (
          <p className="text-sm text-slate-500 py-4">{t("visitors.preauths.qrUnavailable")}</p>
        )}
        <DialogFooter className="gap-2">
          {isManager && preauth?.qr_value && (
            <Button variant="outline" onClick={regenerate}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {t("visitors.preauths.regenerateQr")}
            </Button>
          )}
          {preauth?.qr_value && (
            <Button onClick={download} disabled={!image}>
              <Download className="h-4 w-4 mr-2" />
              {t("visitors.preauths.downloadQr")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
